import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { ProformsAdminService } from '../../../../services/proforms/proforms-admin.service';
import {
  ProFormsAnswerOptionAPIResponse,
  ProFormsAPIResponse,
  ProFormsQuestionAPIResponse,
  ProFormType,
} from '../../../../types/responses/proforms.responses';

interface CheckboxGridFieldTypeSpecs {
  rowNames: string[];
  colNames: string[];
}

@Component({
  selector: 'app-proforms-admin-question-answer-checkbox-grid',
  standalone: false,
  templateUrl: './proforms-admin-question-answer-checkbox-grid.component.html',
  styleUrl: './proforms-admin-question-answer-checkbox-grid.component.scss',
})
export class ProformsAdminQuestionAnswerCheckboxGridComponent
  implements OnInit, OnChanges, OnDestroy
{
  readonly maxRows = 50;

  readonly maxColumns = 10;

  @Input() question: ProFormsQuestionAPIResponse;

  subscriptions = new Subscription();

  rowNames: string[] = [];

  colNames: string[] = [];

  specsChangeSubject = new Subject<void>();

  serializedFieldTypeSpecs: CheckboxGridFieldTypeSpecs;

  isQuiz = false;

  constructor(private proformsAdminService: ProformsAdminService) {}

  ngOnInit() {
    this.serializedFieldTypeSpecs = this.getFieldTypeSpecs();
    this.subscriptions.add(
      this.specsChangeSubject.pipe(debounceTime(500)).subscribe(() => {
        this.persistFieldTypeSpecs();
      })
    );

    this.subscriptions.add(
      this.proformsAdminService.currentProForm.subscribe((response) => {
        if (response) {
          const form: ProFormsAPIResponse = response;
          this.isQuiz = form.type === ProFormType.QUIZ;
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['question']?.currentValue) {
      this.initializeGridOptions();
    }
  }

  onRowNameChange(index: number, title: string) {
    this.rowNames[index] = title;
    this.specsChangeSubject.next();
  }

  onColNameChange(index: number, title: string) {
    this.colNames[index] = title;
    this.specsChangeSubject.next();
  }

  trackByIndex(index: number): number {
    return this.question ? index : index;
  }

  deleteRow(index: number) {
    if (this.rowNames.length <= 1) return;
    this.rowNames.splice(index, 1);
    this.persistFieldTypeSpecs();
  }

  deleteColumn(index: number) {
    if (this.colNames.length <= 1) return;
    this.colNames.splice(index, 1);
    this.persistFieldTypeSpecs();
  }

  addRow() {
    if (!this.canAddRow) {
      return;
    }

    const nextIndex = this.rowNames.length + 1;
    this.rowNames.push(`Row ${nextIndex}`);
    this.persistFieldTypeSpecs();
  }

  addColumn() {
    if (!this.canAddColumn) {
      return;
    }

    const nextIndex = this.colNames.length + 1;
    this.colNames.push(`Column ${nextIndex}`);
    this.persistFieldTypeSpecs();
  }

  get canAddRow(): boolean {
    return this.rowNames.length < this.maxRows;
  }

  get canAddColumn(): boolean {
    return this.colNames.length < this.maxColumns;
  }

  initializeGridOptions() {
    if (!this.question) return;
    const specs = this.getFieldTypeSpecs();
    this.rowNames = specs.rowNames.length > 0 ? [...specs.rowNames] : ['Row 1'];
    this.colNames =
      specs.colNames.length > 0 ? [...specs.colNames] : ['Column 1'];
    this.serializedFieldTypeSpecs = {
      rowNames: [...this.rowNames],
      colNames: [...this.colNames],
    };
  }

  getFieldTypeSpecs(): CheckboxGridFieldTypeSpecs {
    if (!this.question?.field_type_specs) {
      return { rowNames: [], colNames: [] };
    }

    try {
      const parsed = JSON.parse(this.question.field_type_specs);

      const rowNames = Array.isArray(parsed?.rowNames) ? parsed.rowNames : [];

      const colNames = Array.isArray(parsed?.colNames) ? parsed.colNames : [];

      return {
        rowNames,
        colNames,
      };
    } catch {
      return { rowNames: [], colNames: [] };
    }
  }

  persistFieldTypeSpecs() {
    const specs: CheckboxGridFieldTypeSpecs = {
      rowNames: [...this.rowNames],
      colNames: [...this.colNames],
    };

    if (specs.rowNames.length === 0 || specs.colNames.length === 0) {
      return;
    }

    const fieldTypeSpecs = JSON.stringify(specs);
    this.question.field_type_specs = fieldTypeSpecs;
    this.serializedFieldTypeSpecs = {
      rowNames: [...specs.rowNames],
      colNames: [...specs.colNames],
    };

    this.proformsAdminService.updateQuestion(
      this.question.id,
      this.question.proform_form_section_id,
      { field_type_specs: fieldTypeSpecs }
    );
  }

  getCheckStatus(row: string, col: string): number {
    return this.getAnswerOptionForCell(row, col)?.is_correct ?? 0;
  }

  getOptionId(row: string, col: string): number {
    return this.getAnswerOptionForCell(row, col)?.id ?? -1;
  }

  onAnswerClicked(optionId: number): void {
    if (!this.isQuiz) {
      return;
    }

    const selectedOption = this.question?.answerOptions.find(
      (option) => option.id === optionId
    );

    if (selectedOption) {
      selectedOption.is_correct = 1;
    }
  }

  private getAnswerOptionForCell(
    row: string,
    col: string
  ): ProFormsAnswerOptionAPIResponse | undefined {
    if (!this.question?.answerOptions?.length) {
      return undefined;
    }

    const byEncodedAnswer = this.question.answerOptions.find((option) =>
      [
        `${row}|||${col}`,
        `${row}::${col}`,
        `${row}|${col}`,
        `${row},${col}`,
        `${row} - ${col}`,
      ].includes(option.answer)
    );

    if (byEncodedAnswer) {
      return byEncodedAnswer;
    }

    const rowIndex =
      this.serializedFieldTypeSpecs?.rowNames?.indexOf(row) ?? -1;
    const colIndex =
      this.serializedFieldTypeSpecs?.colNames?.indexOf(col) ?? -1;

    if (rowIndex < 0 || colIndex < 0) {
      return undefined;
    }

    const colCount = this.serializedFieldTypeSpecs.colNames.length;
    const answerOptionIndex = rowIndex * colCount + colIndex;

    return this.question.answerOptions[answerOptionIndex];
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
